import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { CartContext } from '../../context/CartContext';
import MenuSection from '../../components/POS/Menu/MenuSection_design1';
import OrderSummary from '../../components/POS/Cart/OrderSummary';
import ReceiptDisplay from '../../components/POS/Receipt/ReceiptDisplay';
import PrintReceiptModal from '../../components/POS/Receipt/PrintReceiptModal';
import KOTReceiptDisplay from '../../components/POS/KOT/KOTReceiptDisplay';
import RecentOrdersModal from '../../components/POS/Modals/RecentOrdersModal';
import Header from '../../components/shared/Header_design1';
import { useTables } from '../../hooks/useTables';
import { useTableSplits } from '../../hooks/useTableSplits';
import { useKOT } from '../../hooks/useKOT';
import { kotService } from '../../services/kotService';
import { customerService } from '../../services/customerService';
import useBranch from '../../hooks/useBranch';
import printService from '../../services/printService';
import receiptConfigurationService from '../../services/receiptConfigurationService';
import branchService from '../../services/branchService';
import { generateKOTContent } from '../../utils/receiptFormatter';
import { COUNTER_TABLE_NAME, COUNTER_SECTION_NAME, COUNTER_DEFAULTS } from '../../config/counterConfig';
import tableSplitService from '../../services/tableSplitService';
import { useAuth } from '../../context/AuthContext';
import { P } from '../../config/permissionCodes';

import { toast } from 'react-hot-toast';
import {
  UtensilsCrossed, Clock, TrendingUp, Users, Minimize,
  ArrowLeft, CheckCircle2, FilePlus, Pause, Trash2,
  Printer, Receipt, AlertCircle, RefreshCw, MoreVertical, XCircle,
  Split, Maximize, PrinterCheck, Coffee, Hash, Keyboard, X, Send
} from 'lucide-react';

export default function POSScreenDesign1() {
  const { tableId: tableIdParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const browseMode = location.state?.browseMode === true;
  const isCounterMode = tableIdParam === 'counter' || location.state?.isCounterMode === true;

  const { tables: apiTables, loading: tablesLoading } = useTables();
  const { splits, fetchSplits, createSplitForTable, updatePeopleCount, updateSplitStatus, deleteSplit } = useTableSplits();
  const { branch } = useBranch();
  const { hasPermission } = useAuth();

  const {
    getCurrentCartItems, calculateTotal, currentTableId, currenttableSplit,
    createNewOrder, createOrderWithItems, switchToOrder, getTableOrders,
    holdOrder, resumeHeldOrder, orderStatus, clearAllOrders, getCurrentOrder,
    cancelOrder, getNexttableSplit, updateOrderPax, sendKOT, hasNewItemsForKOT,
    getNextKOTNumber, addToCart, repeatOrder, linkedCustomer, setLinkedCustomer, linkedWaiter,
  } = useContext(CartContext);

  const { activeOrders, loading: ordersLoading, refreshActiveOrders } = useKOT(
    currentTableId, currenttableSplit, { skipIfNew: false }
  );

  // ─── Permissions ───────────────────────────────────────────────────────────
  const canAccessDineInScreen = hasPermission(P.POS.DINE_IN.SCREEN);
  const canSendKOTPerm = (hasPermission(P.POS.DINE_IN.CREATE) || hasPermission(P.POS.DINE_IN.EDIT));
  const canPrintKOTPerm = (hasPermission(P.POS.DINE_IN.CREATE) || hasPermission(P.POS.DINE_IN.EDIT));
  const canHoldOrder = hasPermission(P.POS.DINE_IN.EDIT);
  const canSettle = hasPermission(P.POS.DINE_IN.SETTLE);
  const canSplitTable = hasPermission(P.POS.DINE_IN.SPLIT);
  const canCancelOrder = hasPermission(P.POS.DINE_IN.CANCEL);
  const canCreateOrder = hasPermission(P.POS.DINE_IN.CREATE);
  const canPrintBill = hasPermission(P.POS.DINE_IN.PRINT_BILL);
  const canSendEBill  = hasPermission(P.POS.DINE_IN.PRINT_BILL);
  const canReprint = hasPermission(P.POS.DINE_IN.REPRINT);
  const canApplyPromo    = hasPermission(P.POS.DINE_IN.PROMO);
  const canApplyDiscount = hasPermission(P.POS.DINE_IN.DISCOUNT);

  // ─── Component state ───────────────────────────────────────────────────────
  const [isMaximized, setIsMaximized] = useState(!!document.fullscreenElement);
  const [isInitializing, setIsInitializing] = useState(true);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitPax, setSplitPax] = useState(2);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isEditingPax, setIsEditingPax] = useState(false);
  const [paxEditValue, setPaxEditValue] = useState('');
  const [showKOTReceipt, setShowKOTReceipt] = useState(false);
  const [kotReceiptData, setKotReceiptData] = useState(null);
  const [showRecentOrders, setShowRecentOrders] = useState(false);
  const [showPrintReceipt, setShowPrintReceipt] = useState(false);
  const [showEBillModal, setShowEBillModal] = useState(false);
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  const [showNewOrderConfirm, setShowNewOrderConfirm] = useState(false);
  const [showDraftKOTConfirm, setShowDraftKOTConfirm] = useState(false);
  const [draftKOTAction, setDraftKOTAction] = useState(null);
  const [isSendingDraftKOT, setIsSendingDraftKOT] = useState(false);
  const [initAttempted, setInitAttempted] = useState(false);

  const counterTable = React.useMemo(() => {
    if (!isCounterMode || tablesLoading) return null;
    const foundCounter = apiTables.find(t =>
      (t.tableNumber || t.name || '').toUpperCase() === COUNTER_TABLE_NAME
    );
    if (foundCounter) {
      return {
        id: foundCounter.id,
        name: COUNTER_TABLE_NAME,
        tableNumber: COUNTER_TABLE_NAME,
        section: foundCounter.section?.name || COUNTER_SECTION_NAME,
        capacity: foundCounter.seatingCapacity || COUNTER_DEFAULTS.capacity,
        status: foundCounter.status || COUNTER_DEFAULTS.status
      };
    }
    console.warn('COUNTER table not found in database. Using virtual table.');
    return {
      id: null, name: COUNTER_TABLE_NAME, tableNumber: COUNTER_TABLE_NAME,
      section: COUNTER_SECTION_NAME, capacity: COUNTER_DEFAULTS.capacity, status: COUNTER_DEFAULTS.status
    };
  }, [isCounterMode, apiTables, tablesLoading]);

  const tableId = isCounterMode ? (counterTable?.id || null) : parseInt(tableIdParam);
  const currentTable = isCounterMode ? counterTable : apiTables.find(t => t.id === tableId);
  const currentOrder = getCurrentOrder();
  const tableOrders = getTableOrders(tableId);
  const canSplit = getNexttableSplit(tableId) !== null;
  const isTableOccupied = tableOrders.length > 0;

  const currentSplit = splits.find(
    s => s.tableId === currentTableId &&
         (s.splitName === currenttableSplit ||
          s.splitName === `Split ${currenttableSplit}`)
  );
  const isOrderHeld = currentSplit?.status === 'hold' ||
                      currentSplit?.status === 'HOLD' ||
                      currentSplit?.status === 'HELD';
  const isBillPrinted = currentSplit?.status === 'payment_pending';
  const canSendKOT = hasNewItemsForKOT();
  const hasActiveOrders = activeOrders && activeOrders.length > 0;
  const hasCartItems = getCurrentCartItems().length > 0;
  const canFinalize = (hasActiveOrders || hasCartItems) && !ordersLoading;

  useEffect(() => {
    if (initAttempted && currentTableId === tableId) return;
    if (tablesLoading) return;

    if (!currentTable) {
      if (isCounterMode) {
        console.error('❌ COUNTER table not found in database');
        toast.error('Counter table not configured. Please contact admin.');
      } else {
        console.error('❌ Invalid table:', { tableId, tableIdParam, currentTable });
        toast.error('Invalid table selected');
      }
      navigate('/pos/tables');
      return;
    }

    const navigationState = location.state || {};
    const hasNavigationState = !!(navigationState.split || navigationState.pax || navigationState.tableId);
    const splitFromNav = isCounterMode
      ? COUNTER_DEFAULTS.split
      : (hasNavigationState ? (navigationState.split || 'A') : (currenttableSplit || 'A'));
    const paxFromNav = isCounterMode
      ? COUNTER_DEFAULTS.pax
      : (hasNavigationState ? (navigationState.pax || 1) : 1);

    const isDifferentTable = currentTableId !== tableId;
    const isDifferentSplit = currenttableSplit !== splitFromNav;
    const hasNoCurrentOrder = !getCurrentOrder();
    const needsInitialization = hasNavigationState || isDifferentTable || isDifferentSplit || hasNoCurrentOrder;

    if (needsInitialization) {
      const initializeTableFromAPI = async () => {
        setInitAttempted(true);
        try {
          const response = await kotService.getActiveOrders(tableId, splitFromNav);
          let ordersData = [];
          if (Array.isArray(response)) ordersData = response;
          else if (Array.isArray(response?.data?.data)) ordersData = response.data.data;
          else if (Array.isArray(response?.data)) ordersData = response.data;
          else if (response?.data?.data) ordersData = [response.data.data];
          else if (response?.data) ordersData = [response.data];

          if (Array.isArray(ordersData) && ordersData.length > 0) {
            const backendOrder = ordersData[0];
            createOrderWithItems(currentTable, paxFromNav, splitFromNav, backendOrder.items || []);
            if (backendOrder.customerId && !linkedCustomer) {
              try {
                const cust = await customerService.getById(backendOrder.customerId);
                if (cust) setLinkedCustomer(cust);
              } catch {}
            }
          } else {
            if (isCounterMode) {
              try {
                const newSplit = await createSplitForTable(tableId, {
                  isCounterMode: true,
                  peopleCount: COUNTER_DEFAULTS.pax,
                });
                const splitName = newSplit?.splitName || COUNTER_DEFAULTS.split;
                createNewOrder(currentTable, COUNTER_DEFAULTS.pax, false, splitName);
              } catch (splitError) {
                console.warn('⚠️ Could not create counter split:', splitError.message);
                createNewOrder(currentTable, COUNTER_DEFAULTS.pax, false, COUNTER_DEFAULTS.split);
              }
            } else {
              const existingSplits = await fetchSplits(tableId);
              const targetSplit = existingSplits?.find(
                s => (s.splitName === `Split ${splitFromNav}` || s.splitName === splitFromNav) &&
                     (s.status === 'active' || s.status === 'hold' || s.status === 'HOLD' || s.status === 'HELD')
              );
              if (!targetSplit && splitFromNav === 'A') {
                await createSplitForTable(tableId, {
                  splitName: 'Split A',
                  peopleCount: paxFromNav,
                  status: 'active',
                });
              }
              createNewOrder(currentTable, paxFromNav, false, splitFromNav);
            }
          }
        } catch (error) {
          console.error('❌ Error in initializeTableFromAPI:', error);
          createNewOrder(currentTable, isCounterMode ? 1 : paxFromNav, false, splitFromNav);
        }
      };
      initializeTableFromAPI();
    }
    setIsInitializing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId, tablesLoading, isCounterMode]);

  useEffect(() => {
    if (tableId && !tablesLoading) fetchSplits(tableId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId, tablesLoading]);

  useEffect(() => {
    if (!isInitializing && location.state?.autoFinalize && hasActiveOrders) {
      const timer = setTimeout(() => setShowReceipt(true), 500);
      return () => clearTimeout(timer);
    }
  }, [isInitializing, location.state, hasActiveOrders]);

  useEffect(() => {
    if (!isInitializing && location.state?.autoSettle && hasActiveOrders) {
      const timer = setTimeout(() => setShowReceipt(true), 500);
      return () => clearTimeout(timer);
    }
  }, [isInitializing, location.state, hasActiveOrders]);

  const toggleMaximize = useCallback(() => {
    const nextMaximizedState = !isMaximized;
    setIsMaximized(nextMaximizedState);
    if (nextMaximizedState) {
      document.documentElement.requestFullscreen().catch(err => {
        console.warn("Could not enter native fullscreen mode:", err);
      });
    } else {
      if (document.fullscreenElement) document.exitFullscreen();
    }
  }, [isMaximized]);

  useEffect(() => {
    const handleFullscreenChange = () => setIsMaximized(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const cartItems = getCurrentCartItems();
  const currentBillTotal = calculateTotal();

  const cleanupEmptySplitAndNavigate = async (destination) => {
    if (browseMode) { navigate(destination); return; }
    try {
      const splitId = currentSplit?.id;
      const hasDraftItems = hasNewItemsForKOT();
      const hasSentOrders = activeOrders && activeOrders.length > 0;
      if (isCounterMode) {
        if (!hasSentOrders) {
          const counterTableId = counterTable?.id;
          if (counterTableId) await tableSplitService.cleanupCounterSplits(counterTableId);
        }
      } else {
        if (splitId && !hasSentOrders && !hasDraftItems) {
          await tableSplitService.deleteSplit(splitId);
        }
      }
    } catch (error) {
      console.warn('Split cleanup failed (non-critical):', error);
    }
    navigate(destination);
  };

  const handleBackToTables = () => {
    cleanupEmptySplitAndNavigate(isCounterMode ? '/pos' : '/pos/tables');
  };

  const handleNewOrder = async () => {
    if (isCounterMode) {
      if (cartItems.length > 0) { setShowNewOrderConfirm(true); return; }
      await startFreshCounterOrder();
    } else {
      toast.success(`Items saved on ${currentTable.name}. Select new table.`);
      cleanupEmptySplitAndNavigate('/pos/tables');
    }
  };

  const startFreshCounterOrder = async () => {
    const counterTableId = counterTable?.id;
    let splitName = COUNTER_DEFAULTS.split;
    if (counterTableId) {
      try {
        const newSplit = await createSplitForTable(counterTableId, {
          isCounterMode: true,
          peopleCount: COUNTER_DEFAULTS.pax,
        });
        splitName = newSplit?.splitName || COUNTER_DEFAULTS.split;
      } catch (splitError) {
        console.warn('⚠️ Could not create counter split:', splitError.message);
      }
    }
    createNewOrder(currentTable, COUNTER_DEFAULTS.pax, false, splitName, true);
    setInitAttempted(false);
    toast.success('New counter order started');
  };

  const handleNewOrderCancel = async () => {
    setShowNewOrderConfirm(false);
    try {
      await cancelOrder(currentTableId, currenttableSplit);
      await startFreshCounterOrder();
    } catch (error) {
      console.error('Error cancelling order:', error);
      toast.error('Failed to cancel order');
    }
  };

  const handleNewOrderFinalize = () => {
    setShowNewOrderConfirm(false);
    setTimeout(() => setShowReceipt(true), 0);
  };

  const handleResumeOrder = async () => {
    if (!canHoldOrder) { toast.error('You do not have permission to resume orders', { icon: '🚫' }); return; }
    try {
      resumeHeldOrder(currentTableId, currenttableSplit);
      if (currentTableId && currenttableSplit && currentSplit && currentSplit.id) {
        await updateSplitStatus(currentSplit.id, 'active', currentTableId);
      }
      toast.success(`Order for ${currentTable.name}${currenttableSplit ? `-${currenttableSplit}` : ''} is now **ACTIVE** and resumed.`);
    } catch (error) {
      console.error('Error resuming order:', error);
      toast.error('Failed to update split status');
    }
  };

  const handleHoldAndExit = async () => {
    if (!canHoldOrder) { toast.error('You do not have permission to hold orders', { icon: '🚫' }); return; }
    try {
      holdOrder();
      if (currentTableId && currenttableSplit && splits.length > 0) {
        const currentSplit = splits.find(
          (s) => s.tableId === currentTableId &&
            (s.splitName === currenttableSplit || s.splitName === `Split ${currenttableSplit}`)
        );
        if (currentSplit && currentSplit.id) {
          await updateSplitStatus(currentSplit.id, 'hold', currentTableId);
        }
      }
      toast.success(`Order for ${currentTable.name} is now on HOLD.`);
      navigate('/pos/tables');
    } catch (error) {
      console.error('Error holding order:', error);
      toast.error('Failed to update split status');
      navigate('/pos/tables');
    }
  };

  const handleCancelOrder = () => {
    if (!canCancelOrder) { toast.error('You do not have permission to cancel orders', { icon: '🚫' }); setShowActionsMenu(false); return; }
    if (!currentTableId || (!isTableOccupied && cartItems.length === 0)) {
      toast.error('No active order to cancel.'); setShowActionsMenu(false); return;
    }
    setShowActionsMenu(false);
    setShowCancelConfirm(true);
  };

  const confirmCancelOrder = async () => {
    setIsCancelling(true);
    try {
      await cancelOrder(currentTableId, currenttableSplit);
      toast.success(`✓ Order for ${currentTable.name}${currenttableSplit ? `-${currenttableSplit}` : ''} cancelled successfully`, { duration: 3000 });
      setShowCancelConfirm(false);
      if (isCounterMode) {
        const counterTableId = counterTable?.id;
        let splitName = COUNTER_DEFAULTS.split;
        if (counterTableId) {
          try {
            const newSplit = await createSplitForTable(counterTableId, {
              isCounterMode: true, peopleCount: COUNTER_DEFAULTS.pax,
            });
            splitName = newSplit?.splitName || COUNTER_DEFAULTS.split;
          } catch (splitError) {
            console.warn('⚠️ Could not create counter split:', splitError.message);
          }
        }
        createNewOrder(currentTable, COUNTER_DEFAULTS.pax, false, splitName, true);
        setInitAttempted(false);
        toast.success('Ready for new counter order');
      } else {
        const remainingOrders = getTableOrders(currentTableId).filter(
          o => !(o.tableId === currentTableId && o.tableSplit === currenttableSplit)
        );
        if (remainingOrders.length > 0) {
          switchToOrder(remainingOrders[0].tableId, remainingOrders[0].tableSplit);
        } else {
          navigate('/pos/tables');
        }
      }
    } catch (error) {
      console.error('Cancel order failed:', error);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleClearAllOrders = () => {
    setShowActionsMenu(false);
    setShowClearConfirm(true);
  };

  const confirmClearAllOrders = async () => {
    if (typeof clearAllOrders === 'function') {
      clearAllOrders();
      toast.success('All orders cleared');
      if (isCounterMode) {
        const counterTableId = counterTable?.id;
        let splitName = COUNTER_DEFAULTS.split;
        if (counterTableId) {
          try {
            const newSplit = await createSplitForTable(counterTableId, {
              isCounterMode: true, peopleCount: COUNTER_DEFAULTS.pax,
            });
            splitName = newSplit?.splitName || COUNTER_DEFAULTS.split;
          } catch (splitError) {
            console.warn('⚠️ Could not create counter split:', splitError.message);
          }
        }
        createNewOrder(currentTable, COUNTER_DEFAULTS.pax, false, splitName, true);
        setInitAttempted(false);
      } else {
        navigate('/pos/tables');
      }
    }
    setShowClearConfirm(false);
  };

  const handleFinalizePrint = () => {
    if (!canSettle) { toast.error('You do not have permission to settle payments', { icon: '🚫' }); return; }
    if (!canFinalize) { toast.error('No active orders to settle', { icon: '💳' }); return; }
    if (!isCounterMode && !isBillPrinted) { toast.error('Please print receipt first (F8)', { icon: '🧾' }); return; }
    if (canSendKOT) { setDraftKOTAction('settle'); setShowDraftKOTConfirm(true); return; }
    setShowReceipt(true);
  };

  const handlePrintReceipt = () => {
    if (!canPrintBill) { toast.error('You do not have permission to print receipts', { icon: '🚫' }); return; }
    if (!canFinalize) { toast.error('No active orders to print receipt', { icon: '🧾' }); return; }
    if (canSendKOT) { setDraftKOTAction('printReceipt'); setShowDraftKOTConfirm(true); return; }
    setShowPrintReceipt(true);
  };

  const handleSendEBill = () => {
    if (!canSendEBill) { toast.error('You do not have permission to send e-Bills', { icon: '🚫' }); return; }
    if (!canFinalize) { toast.error('No active orders to send e-Bill', { icon: '📱' }); return; }
    if (canSendKOT) { setDraftKOTAction('eBill'); setShowDraftKOTConfirm(true); return; }
    setShowEBillModal(true);
  };

  const handleDraftKOTConfirm = async () => {
    setIsSendingDraftKOT(true);
    try {
      const success = await sendKOT(activeOrders?.length || 0);
      if (!success) { toast.error('Failed to send items to kitchen. Please try again.'); setIsSendingDraftKOT(false); return; }
      await refreshActiveOrders();
      setShowDraftKOTConfirm(false);
      setIsSendingDraftKOT(false);
      if (draftKOTAction === 'printReceipt') setShowPrintReceipt(true);
      else if (draftKOTAction === 'eBill') setShowEBillModal(true);
      else if (draftKOTAction === 'settle') setShowReceipt(true);
      setDraftKOTAction(null);
    } catch (error) {
      console.error('Draft KOT send failed:', error);
      toast.error('Failed to send items to kitchen');
      setIsSendingDraftKOT(false);
    }
  };

  const handlePrintSuccess = async () => {
    await fetchSplits(currentTableId);
    toast.success('Receipt printed! Click Settle Payment (F4) to complete.', { icon: '✅', duration: 3000 });
  };

  const kotModes = branch?.kotModes || ['PRINT'];
  const shouldPrintKOT = kotModes.includes('PRINT');
  const SHOW_KOT_PREVIEW = false;

  const directPrintKOT = async (draftItems, kotNumber) => {
    try {
      const [receiptConfig, branchData] = await Promise.all([
        receiptConfigurationService.getDefault('KOT').catch(() => null),
        branchService.getCurrentBranch().catch(() => null),
      ]);
      const formattedBranch = branchData ? branchService.formatBranchForReceipt(branchData) : null;
      const grouped = {};
      draftItems.forEach(item => {
        const station = item.kitchenStationName || 'MAIN KITCHEN';
        if (!grouped[station]) grouped[station] = [];
        grouped[station].push(item);
      });
      const stations = Object.keys(grouped);
      const timestamp = new Date().toLocaleString('en-IN', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
      const printPromises = stations.map(async (station) => {
        const kotPrintData = {
          restaurantName: formattedBranch?.name || 'Restaurant',
          kotNumber: kotNumber || 'N/A',
          tableName: currentTable?.name || 'N/A',
          tableSplit: currenttableSplit,
          pax: currentOrder?.pax,
          stationName: station,
          waiterName: linkedWaiter?.name || null,
          timestamp,
          items: grouped[station].map(item => ({
            name: item.name, quantity: item.quantity, customization: item.customization,
          })),
          chefNotes: currentOrder?.orderNotes?.chefNotes,
          allergies: currentOrder?.orderNotes?.allergies,
        };
        const kotResult = generateKOTContent(receiptConfig || {}, kotPrintData);
        return printService.printKOTToStation(station, {
          formattedContent: kotResult.content,
          printType: 'KOT',
          kitchenStationName: station,
          content: {
            kotNumber: kotPrintData.kotNumber,
            tableName: kotPrintData.tableName,
            tableSplit: kotPrintData.tableSplit,
            pax: kotPrintData.pax,
            stationName: station,
            timestamp: kotPrintData.timestamp,
            items: kotPrintData.items,
            chefNotes: kotPrintData.chefNotes,
            allergies: kotPrintData.allergies,
          },
        });
      });
      const results = await Promise.all(printPromises);
      const allSuccess = results.every(r => r?.success);
      if (allSuccess) {
        toast.success(`KOT printed to ${stations.length} station${stations.length > 1 ? 's' : ''}`, { duration: 2000 });
      } else {
        toast.error('Some KOT prints failed', { duration: 3000 });
      }
    } catch (error) {
      console.error('Direct KOT print error:', error);
      toast.error('KOT print failed');
    }
  };

  const handleSendKOT = async () => {
    if (!canSendKOTPerm) { toast.error('You do not have permission to send KOTs', { icon: '🚫' }); return; }
    if (!canSendKOT) { toast.error('No new items to send! All items already in kitchen.', { duration: 3000, icon: '👨‍🍳' }); return; }
    const draftItems = cartItems.filter(item => !item.sentToKitchen);
    if (draftItems.length === 0) { toast.error('No items to send!'); return; }
    if (shouldPrintKOT && SHOW_KOT_PREVIEW) {
      const nextKOTNumber = getNextKOTNumber(currentOrder, activeOrders?.length || 0);
      setKotReceiptData({ items: draftItems, kotNumber: nextKOTNumber, table: currentTable });
      setShowKOTReceipt(true);
    } else {
      const nextKOTNumber = getNextKOTNumber(currentOrder, activeOrders?.length || 0);
      const success = sendKOT(activeOrders?.length || 0);
      if (success && shouldPrintKOT) {
        if (canPrintKOTPerm) await directPrintKOT(draftItems, nextKOTNumber);
      }
    }
  };

  const handleConfirmKOTPrint = () => {
    const success = sendKOT(activeOrders?.length || 0);
    if (success) {
      const latestKOT = getCurrentOrder()?.kots?.[getCurrentOrder().kots.length - 1];
      setKotReceiptData(prev => ({ ...prev, kotNumber: latestKOT?.kotNumber || prev.kotNumber }));
      return true;
    }
    return false;
  };

  const handleStartEditPax = () => {
    if (currentOrder) {
      const paxValue = currentOrder.pax > 0 ? currentOrder.pax : 2;
      setPaxEditValue(paxValue.toString());
      setIsEditingPax(true);
    }
  };

  const handleSavePax = async () => {
    try {
      const newPax = Number(paxEditValue);
      if (!newPax || newPax < 1) { toast.error("Guest count must be at least 1"); return; }
      const tableSplitValue = currentOrder.tableSplit || 'A';
      const split = splits.find((s) => {
        const splitName = s.splitName?.trim()?.toLowerCase() || '';
        const orderSplit = tableSplitValue?.trim()?.toLowerCase() || '';
        return splitName === orderSplit || splitName === `split ${orderSplit}` || splitName.endsWith(` ${orderSplit}`);
      });
      if (!split) { toast.error("Split not found. Please try again."); return; }
      const response = await updatePeopleCount(currentOrder.tableId, split, newPax);
      updateOrderPax(currentOrder.tableId, currentOrder.tableSplit, newPax);
      await fetchSplits(currentOrder.tableId);
      setIsEditingPax(false);
      toast.success(`Guest count updated to ${newPax}`);
    } catch (error) {
      console.error("Error updating pax:", error);
      toast.error("Failed to update guest count");
    }
  };

  const handleCancelEditPax = () => { setIsEditingPax(false); setPaxEditValue(''); };

  const handleShowRecentOrders = () => setShowRecentOrders(true);

  const handleAddItemsToCart = (items) => {
    if (!items || items.length === 0) { toast.error('No items to add'); return; }
    if (!currentTableId) { toast.error('Please select a table first'); return; }
    const currentOrder = getCurrentOrder();
    if (!currentOrder) { toast.error('No active order. Please start an order first.'); return; }
    const expandedItems = [];
    items.forEach(item => {
      const quantity = item.quantity || 1;
      for (let i = 0; i < quantity; i++) {
        expandedItems.push({
          id: item.id, name: item.name, price: item.price, finalPrice: item.price,
          variantId: item.variantId, variantName: item.variantName,
          customization: item.customization, quantity: 1, sentToKitchen: false,
        });
      }
    });
    repeatOrder({ items: expandedItems });
    setShowRecentOrders(false);
  };

  const handleStartSplit = () => {
    if (!canSplitTable) { toast.error('You do not have permission to split tables', { icon: '🚫' }); return; }
    if (!currentTableId || !isTableOccupied) { toast.error('Cannot split an empty table.'); return; }
    if (!canSplit) { toast.error('Maximum number of splits reached for this table.'); return; }
    setSplitPax(Math.min(2, currentTable.capacity || 2));
    setShowSplitModal(true);
  };

  const confirmSplitOrder = async () => {
    if (!currentTable) { toast.error("No table selected"); return; }
    if (splitPax < 1) { toast.error("Split must have at least 1 guest."); return; }
    try {
      const latestSplits = await fetchSplits(currentTable.id);
      const activeSplits = (latestSplits || []).filter(s => s.status === "active");
      const nextSplitCount = activeSplits.length + 1;
      const splitName = `Split ${String.fromCharCode(64 + nextSplitCount)}`;
      const newSplit = await createSplitForTable(currentTable.id, {
        splitName, peopleCount: splitPax, status: "active",
      });
      if (newSplit) {
        createNewOrder(currentTable, splitPax, true);
        toast.success(`${splitName} created successfully!`);
      }
      setShowSplitModal(false);
    } catch (error) {
      console.error("❌ Error creating split:", error);
      toast.error("Could not create table split");
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;
      if (showReceipt || showClearConfirm || showSplitModal || showCancelConfirm || showKOTReceipt || showRecentOrders || showNewOrderConfirm || showPrintReceipt || showEBillModal || showDraftKOTConfirm) return;

      switch (e.key) {
        case 'F1':
          e.preventDefault();
          if (!canSendKOTPerm) toast.error('No permission to send KOT', { icon: '🚫' });
          else if (isBillPrinted) toast.error('Bill already printed - Cannot add items', { icon: '🧾' });
          else if (canSendKOT) handleSendKOT();
          else toast.error('No new items to send to kitchen', { icon: '👨‍🍳' });
          break;
        case 'F3':
          e.preventDefault();
          if (!isCounterMode) {
            if (!canHoldOrder) toast.error('No permission to hold orders', { icon: '🚫' });
            else if (isOrderHeld) handleResumeOrder();
            else handleHoldAndExit();
          } else toast.error('Hold not available in Counter mode', { icon: '☕' });
          break;
        case 'F4':
          e.preventDefault();
          if (!canSettle) toast.error('No permission to settle payments', { icon: '🚫' });
          else if (!canFinalize) toast.error('No active orders to settle', { icon: '📋' });
          else if (!isCounterMode && !isBillPrinted) toast.error('Please print receipt first (F8)', { icon: '🧾' });
          else handleFinalizePrint();
          break;
        case 'F5':
          e.preventDefault();
          if (!canSplitTable) toast.error('No permission to split tables', { icon: '🚫' });
          else if (isCounterMode) toast.error('Split not available in Counter mode', { icon: '☕' });
          else if (!isTableOccupied) toast.error('Cannot split an empty table', { icon: '⚠️' });
          else if (!canSplit) toast.error('Maximum splits reached for this table', { icon: '⚠️' });
          else handleStartSplit();
          break;
        case 'F6':
          e.preventDefault();
          handleCancelOrder();
          break;
        case 'F7':
          e.preventDefault();
          if (!canCreateOrder) toast.error('No permission to create new orders', { icon: '🚫' });
          else handleNewOrder();
          break;
        case 'F8':
          e.preventDefault();
          if (!canPrintBill) toast.error('No permission to print receipts', { icon: '🚫' });
          else if (isCounterMode) toast.error('Use Settle Payment (F4) in Counter mode', { icon: '🧾' });
          else if (isBillPrinted) toast.error('Bill already printed - Use Settle Payment (F4)', { icon: '🧾' });
          else if (canFinalize) handlePrintReceipt();
          else toast.error('No active orders to print receipt', { icon: '🧾' });
          break;
        case 'F9':
          e.preventDefault();
          if (!canSendEBill) toast.error('No permission to send e-Bills', { icon: '🚫' });
          else if (isCounterMode) toast.error('Use Settle Payment (F4) in Counter mode', { icon: '📱' });
          else if (isBillPrinted) toast.error('Bill already created', { icon: '📱' });
          else if (canFinalize) handleSendEBill();
          else toast.error('No active orders to send e-Bill', { icon: '📱' });
          break;
        case '?':
          e.preventDefault();
          setShowShortcutsHelp(true);
          break;
        case 'Escape':
          if (showShortcutsHelp) { e.preventDefault(); setShowShortcutsHelp(false); }
          break;
        default: break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    canSendKOTPerm, canHoldOrder, canSettle, canSplitTable, canCancelOrder,
    canCreateOrder, canPrintBill, canSendEBill,
    canSendKOT, canFinalize, isCounterMode, isOrderHeld, isTableOccupied, canSplit, isBillPrinted,
    showReceipt, showClearConfirm, showSplitModal, showCancelConfirm, showKOTReceipt, showRecentOrders, showShortcutsHelp, showNewOrderConfirm, showPrintReceipt, showEBillModal, showDraftKOTConfirm
  ]);

  const screenContainerClass = isMaximized
    ? "h-screen w-screen flex flex-col bg-white overflow-hidden"
    : "h-screen w-screen flex flex-col bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 overflow-hidden";

  if (tablesLoading || isInitializing || !currentTable) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">
            {tablesLoading ? 'Loading tables...' : 'Loading table...'}
          </p>
        </div>
      </div>
    );
  }

  if (browseMode) {
    return (
      <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="bg-white border-b border-slate-200 shadow-sm">
          <div className="px-6 py-4 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-red-700 flex items-center gap-2">
                <UtensilsCrossed size={28} />
                Browse Menu
              </h1>
              <p className="text-sm text-slate-500 mt-0.5">View available items (read-only mode)</p>
            </div>
            <button
              onClick={() => navigate('/pos/tables')}
              className="flex items-center gap-2 px-6 py-3 bg-slate-600 hover:bg-slate-700 text-white font-bold rounded-lg transition-all shadow-md"
            >
              <ArrowLeft size={20} />
              Back to Tables
            </button>
          </div>
        </div>
        <main className="flex-1 p-3 overflow-hidden">
          <div className="h-full bg-white rounded-lg shadow-lg border border-slate-200 overflow-hidden">
            <MenuSection browseMode={true} />
          </div>
        </main>
      </div>
    );
  }
  // Normal Mode: Full POS with table selection
  return (
    <div className={screenContainerClass}>

      <Header
        onBack={handleBackToTables}
        onNewOrder={handleNewOrder}
      />

      <main className="flex-1 flex gap-3 p-3 overflow-hidden">
        <div className="flex-[2.8] flex flex-col bg-white rounded-lg shadow-lg border border-slate-200 overflow-hidden">
          <MenuSection
            browseMode={false}
            canApplyPromo={canApplyPromo}
            canApplyDiscount={canApplyDiscount}
          />
        </div>

        <div className="flex-[1.2] flex flex-col bg-white rounded-lg shadow-lg border border-slate-200 overflow-hidden">
          <OrderSummary
            tableOrders={tableOrders}
            onShowRecentOrders={handleShowRecentOrders}
            canApplyPromo={canApplyPromo}
            canApplyDiscount={canApplyDiscount}
            canAddCustomer={false}
            canAddWaiter={false}
            canReprint={canReprint}
          />
        </div>
      </main>

      <footer className={`border-t shadow-lg flex-shrink-0 ${
        isOrderHeld ? 'bg-amber-50 border-amber-300' : 'bg-slate-50 border-slate-200'
      }`}>
        {/* Hold Banner */}
        {isOrderHeld && (
          <div className="bg-amber-100 border-b border-amber-200 px-4 py-1.5 flex items-center gap-2">
            <AlertCircle size={14} className="text-amber-600" />
            <p className="text-xs font-semibold text-amber-800">Order on HOLD - Click Resume to continue</p>
          </div>
        )}

        <div className="px-4 py-2 flex items-center justify-between gap-4">
         {/* Left: Table Info */}
                   <div className="flex items-center gap-3">
                     {isCounterMode ? (
                       <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-b from-red-500 to-red-600 text-white rounded-lg shadow-sm">
                         <Coffee size={14} />
                         <span className="text-sm font-bold">Counter</span>
                       </div>
                     ) : (
                       <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-white shadow-sm ${
                         isOrderHeld
                           ? 'bg-gradient-to-b from-amber-500 to-amber-600'
                           : 'bg-gradient-to-b from-red-500 to-red-600'
                       }`}>
                         <span className="text-sm font-semibold">{isCounterMode ? 'Takeaway' : currentTable.name}</span>
                         {currenttableSplit && !isCounterMode && (
                           <span className="text-xs font-bold bg-white/25 px-1.5 py-0.5 rounded">{currenttableSplit}</span>
                         )}
                       </div>
                     )}

                     {/* PAX - Compact (guest count, editable) */}
                     {currentOrder && !isCounterMode && (
                       <div className="flex items-center gap-1.5 text-xs text-slate-600">
                         <Users size={12} />
                         {isEditingPax ? (
                           <div className="flex items-center gap-1">
                             <input
                               type="number"
                               min="1"
                               value={paxEditValue}
                               onChange={(e) => setPaxEditValue(e.target.value)}
                               onKeyPress={(e) => {
                                 if (e.key === 'Enter') handleSavePax();
                                 if (e.key === 'Escape') handleCancelEditPax();
                               }}
                               className="w-10 px-1 py-0.5 border border-red-400 rounded text-center text-xs font-bold focus:outline-none"
                               autoFocus
                             />
                             <button onClick={handleSavePax} className="text-red-600 hover:text-red-700 font-bold">✓</button>
                             <button onClick={handleCancelEditPax} className="text-slate-400 hover:text-slate-500">✕</button>
                           </div>
                         ) : (
                           <button
                             onClick={handleStartEditPax}
                             className="font-bold text-red-600 hover:underline"
                             title="Edit guests"
                           >
                             {currentOrder.pax || '-'}
                           </button>
                         )}
                       </div>
                     )}
                   </div>

          {/* Center: Action Buttons */}
          <div className="flex items-center gap-4">

            {/* GROUP 1: Send KOT - GREY */}
            {canSendKOTPerm && (
              <button
                onClick={handleSendKOT}
                disabled={!canSendKOT || isBillPrinted}
                className={`px-3 py-2 text-xs font-semibold rounded-md transition-all flex items-center gap-1.5 shadow-sm ${
                  isBillPrinted || !canSendKOT
                    ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                    : 'bg-slate-600 hover:bg-slate-700 text-white hover:shadow-md'
                }`}
                title={isBillPrinted ? "Bill already printed" : "Send KOT (F1)"}
              >
                <Send size={13} />
                Send KOT
                <kbd className="text-[9px] font-bold bg-slate-800 text-slate-200 px-1 py-0.5 rounded">F1</kbd>
              </button>
            )}

            {/* GROUP 2: Billing - RED */}
            {!isCounterMode && (canPrintBill || canSendEBill) && (
              <>
                <span className="text-slate-300 text-lg font-light">|</span>

                {canPrintBill && (
                  <button
                    onClick={handlePrintReceipt}
                    disabled={!canFinalize || isBillPrinted}
                    className={`px-3 py-2 text-xs font-semibold rounded-md transition-all flex items-center gap-1.5 shadow-sm ${
                      isBillPrinted || !canFinalize
                        ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                        : 'bg-gradient-to-b from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white hover:shadow-md'
                    }`}
                    title={isBillPrinted ? "Bill Printed - Use Settle" : "Print Receipt (F8)"}
                  >
                    <Printer size={13} />
                    {isBillPrinted ? 'Bill Printed' : 'Print Receipt'}
                    <kbd className={`text-[9px] font-bold px-1 py-0.5 rounded ${
                      isBillPrinted || !canFinalize ? 'bg-slate-300 text-slate-500' : 'bg-red-800 text-red-100'
                    }`}>F8</kbd>
                  </button>
                )}

                {canSendEBill && (
                  <button
                    onClick={handleSendEBill}
                    disabled={!canFinalize || isBillPrinted}
                    className={`px-3 py-2 text-xs font-semibold rounded-md transition-all flex items-center gap-1.5 shadow-sm ${
                      isBillPrinted || !canFinalize
                        ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                        : 'bg-gradient-to-b from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white hover:shadow-md'
                    }`}
                    title={isBillPrinted ? "Bill already created" : "Send e-Bill via WhatsApp (F9)"}
                  >
                    <Send size={13} />
                    e-Bill
                    <kbd className={`text-[9px] font-bold px-1 py-0.5 rounded ${
                      isBillPrinted || !canFinalize ? 'bg-slate-300 text-slate-500' : 'bg-red-800 text-red-100'
                    }`}>F9</kbd>
                  </button>
                )}
              </>
            )}

            {/* Separator */}
            {(canSendKOTPerm || (!isCounterMode && (canPrintBill || canSendEBill))) && canSettle && (
              <span className="text-slate-400 text-xl font-bold mx-1">|</span>
            )}

            {/* GROUP 3: Settle - RED */}
            {canSettle && (
              <button
                onClick={handleFinalizePrint}
                disabled={!canFinalize || (!isCounterMode && !isBillPrinted)}
                className={`px-4 py-2 text-xs font-bold rounded-md transition-all flex items-center gap-1.5 shadow-sm ${
                  !canFinalize || (!isCounterMode && !isBillPrinted)
                    ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    : 'bg-gradient-to-b from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white hover:shadow-md'
                }`}
                title={!isCounterMode && !isBillPrinted ? "Print receipt first (F8)" : "Settle Payment (F4)"}
              >
                <Receipt size={14} />
                Settle Payment
                <kbd className={`text-[9px] font-bold px-1 py-0.5 rounded ${
                  !canFinalize || (!isCounterMode && !isBillPrinted) ? 'bg-slate-400 text-slate-200' : 'bg-red-800 text-red-100'
                }`}>F4</kbd>
              </button>
            )}

          </div>

          {/* Right: Quick Actions */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setShowShortcutsHelp(true)}
              className="p-1.5 bg-white hover:bg-slate-100 text-slate-500 rounded-md border border-slate-200"
              title="Shortcuts (?)"
            >
              <Keyboard size={14} />
            </button>

            <div className="relative">
              <button
                onClick={() => setShowActionsMenu(!showActionsMenu)}
                className="p-2 bg-gradient-to-b from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg shadow-sm hover:shadow-md flex items-center gap-1.5 transition-all"
                title="More Actions"
                onBlur={() => setTimeout(() => setShowActionsMenu(false), 200)}
              >
                <MoreVertical size={16} />
                <span className="text-xs font-semibold">More</span>
              </button>

              {showActionsMenu && (
                <div className="absolute bottom-full right-0 mb-1 w-44 bg-white rounded-lg shadow-xl ring-1 ring-black/10 z-50 overflow-hidden">
                  <div className="py-0.5">
                    {!isCounterMode && canHoldOrder && (
                      isOrderHeld ? (
                        <button
                          onClick={() => { setShowActionsMenu(false); handleResumeOrder(); }}
                          className="w-full text-left px-3 py-1.5 text-xs text-emerald-600 hover:bg-emerald-50 flex items-center justify-between font-semibold"
                        >
                          <span className="flex items-center gap-2"><RefreshCw size={12} />Resume Order</span>
                          <kbd className="text-[9px] bg-emerald-100 text-emerald-700 px-1 rounded">F3</kbd>
                        </button>
                      ) : (
                        <button
                          onClick={() => { setShowActionsMenu(false); handleHoldAndExit(); }}
                          className="w-full text-left px-3 py-1.5 text-xs text-amber-600 hover:bg-amber-50 flex items-center justify-between font-semibold"
                        >
                          <span className="flex items-center gap-2"><Pause size={12} />Hold Order</span>
                          <kbd className="text-[9px] bg-amber-100 text-amber-700 px-1 rounded">F3</kbd>
                        </button>
                      )
                    )}
                    {!isCounterMode && canSplitTable && isTableOccupied && canSplit && (
                      <button
                        onClick={() => { setShowActionsMenu(false); handleStartSplit(); }}
                        className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 flex items-center justify-between font-semibold"
                      >
                        <span className="flex items-center gap-2"><Split size={12} />Split Table</span>
                        <kbd className="text-[9px] bg-red-100 text-red-700 px-1 rounded">F5</kbd>
                      </button>
                    )}
                    {canCancelOrder && (
                      <button
                        onClick={handleCancelOrder}
                        className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 flex items-center justify-between font-semibold"
                      >
                        <span className="flex items-center gap-2"><XCircle size={12} />Cancel Order</span>
                        <kbd className="text-[9px] bg-red-100 text-red-700 px-1 rounded">F6</kbd>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </footer>

      {/* MODAL 1: Clear All Confirmation */}
      {showClearConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 size={24} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Clear ALL Orders?</h3>
                <p className="text-sm text-slate-600">This action will permanently delete all orders from the system for testing purposes.</p>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowClearConfirm(false)}
                className="flex-1 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg transition"
              >
                No, Keep Orders
              </button>
              <button
                onClick={confirmClearAllOrders}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition"
              >
                Yes, Clear All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 2: Cancel Order Confirmation */}
      {showCancelConfirm && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onKeyDown={(e) => {
            if (isCancelling) return;
            if (e.key === 'Escape') setShowCancelConfirm(false);
            else if (e.key === 'Enter') confirmCancelOrder();
          }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            {isCancelling ? (
              <div className="flex flex-col items-center justify-center py-8 space-y-4">
                <div className="relative">
                  <div className="w-16 h-16 border-4 border-red-200 rounded-full animate-spin border-t-red-600"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <XCircle size={28} className="text-red-600" />
                  </div>
                </div>
                <div className="text-center space-y-2">
                  <h3 className="text-xl font-bold text-slate-900">Cancelling Order...</h3>
                  <p className="text-sm text-slate-600">
                    Please wait while we cancel the order for <strong>{currentTable.name}{currenttableSplit ? `-${currenttableSplit}` : ''}</strong>
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <XCircle size={24} className="text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">
                      Cancel Order for {currentTable.name}{currenttableSplit ? `-${currenttableSplit}` : ''}?
                    </h3>
                    <p className="text-sm text-slate-600">
                      This action is irreversible and will delete all items from this table's current order.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowCancelConfirm(false)}
                    className="flex-1 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg transition focus:outline-none focus:ring-2 focus:ring-slate-400"
                  >
                    Keep Order
                    <span className="ml-2 text-[10px] font-bold bg-slate-300 text-slate-600 px-1.5 py-0.5 rounded">Esc</span>
                  </button>
                  <button
                    onClick={confirmCancelOrder}
                    autoFocus
                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
                  >
                    Yes, Cancel Order
                    <span className="ml-2 text-[10px] font-bold bg-red-800 text-red-100 px-1.5 py-0.5 rounded">Enter</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* MODAL 3: Split Table PAX Selection - RED THEME */}
      {showSplitModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-6">
            <div className="flex items-center gap-4 pb-4 border-b border-slate-200">
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Split size={28} className="text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-slate-900">Split Table {currentTable.name}</h3>
                <p className="text-sm text-slate-600">
                  Creating split <strong>{getNexttableSplit(currentTable.id)}</strong> - How many guests?
                </p>
              </div>
              <button
                onClick={() => setShowSplitModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <XCircle size={20} className="text-slate-500" />
              </button>
            </div>

            <div className="bg-red-50 rounded-xl p-4 text-center">
              <p className="text-sm text-slate-600 mb-1">New Split Label</p>
              <p className="text-4xl font-black text-red-600">{getNexttableSplit(currentTable.id)}</p>
              <p className="text-xs text-slate-500 mt-1">for Table {currentTable.name}</p>
            </div>

            <div>
              <p className="text-sm font-semibold text-slate-700 mb-3">Quick Select:</p>
              <div className="grid grid-cols-4 gap-2">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                  <button
                    key={num}
                    onClick={() => setSplitPax(num)}
                    className={`py-3 px-4 rounded-xl font-bold text-lg transition-all ${
                      splitPax === num
                        ? 'bg-red-600 text-white shadow-lg scale-105'
                        : 'bg-slate-100 text-slate-700 hover:bg-red-100 hover:text-red-700 hover:shadow-md'
                    }`}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Or enter manually:
              </label>
              <input
                type="number"
                min="1"
                value={splitPax}
                onChange={(e) => setSplitPax(Math.max(1, parseInt(e.target.value) || 1))}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') confirmSplitOrder();
                }}
                placeholder="Enter number of guests"
                className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-lg font-semibold text-center"
                autoFocus
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowSplitModal(false)}
                className="flex-1 px-6 py-3 text-slate-600 rounded-xl font-semibold hover:bg-slate-100 transition-all border-2 border-slate-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmSplitOrder}
                disabled={splitPax < 1}
                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-red-600"
              >
                Create Split →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bill Receipt Display */}
      {showReceipt && (
        <ReceiptDisplay
          onClose={() => {
            setShowReceipt(false);
            setShowNewOrderConfirm(false);
          }}
          isCounterMode={isCounterMode}
          onNewOrder={startFreshCounterOrder}
          canReprint={canReprint}
          canApplyPromo={canApplyPromo}
          canApplyDiscount={canApplyDiscount}
        />
      )}

      {/* KOT Receipt Display */}
      {showKOTReceipt && kotReceiptData && (
        <KOTReceiptDisplay
          onClose={() => setShowKOTReceipt(false)}
          kotData={kotReceiptData}
          onConfirmPrint={handleConfirmKOTPrint}
        />
      )}

      {/* Recent Orders Modal */}
      {showRecentOrders && (
        <RecentOrdersModal
          currentTableId={currentTableId}
          onClose={() => setShowRecentOrders(false)}
          onAddToCart={handleAddItemsToCart}
        />
      )}

      {/* Print Receipt Preview Modal */}
      {showPrintReceipt && (
        <PrintReceiptModal
          onClose={() => setShowPrintReceipt(false)}
          onSuccess={handlePrintSuccess}
          canReprint={canReprint}
          canApplyPromo={canApplyPromo}
          canApplyDiscount={canApplyDiscount}
        />
      )}

      {/* e-Bill Modal */}
      {showEBillModal && (
        <PrintReceiptModal
          mode="ebill"
          onClose={() => setShowEBillModal(false)}
          onSuccess={handlePrintSuccess}
          canApplyPromo={canApplyPromo}
          canApplyDiscount={canApplyDiscount}
        />
      )}

      {/* Counter Mode - New Order Confirmation Modal - RED THEME */}
      {showNewOrderConfirm && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onKeyDown={(e) => {
            if (e.key === 'Escape') setShowNewOrderConfirm(false);
          }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5">
            <div className="flex items-center gap-4 pb-4 border-b border-slate-200">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertCircle size={28} className="text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-slate-900">Order Already Running</h3>
                <p className="text-sm text-slate-600 mt-1">
                  You have {cartItems.length} item{cartItems.length > 1 ? 's' : ''} in the current order. What would you like to do?
                </p>
              </div>
            </div>

            <div className="bg-slate-50 rounded-xl p-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">Current Order Total</span>
                <span className="text-2xl font-bold text-slate-900">₹{currentBillTotal.toFixed(2)}</span>
              </div>
            </div>

            <div className="space-y-3">
              {canSettle && (
                <button
                  onClick={handleNewOrderFinalize}
                  autoFocus
                  className="w-full px-5 py-4 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold rounded-xl transition-all shadow-lg hover:shadow-xl flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
                >
                  <div className="flex items-center gap-3">
                    <Receipt size={22} />
                    <div className="text-left">
                      <div className="font-bold">Settle Payment</div>
                      <div className="text-xs text-red-100 font-normal">Complete current order first</div>
                    </div>
                  </div>
                  <span className="text-[10px] font-bold bg-red-800 text-red-100 px-2 py-1 rounded">Enter</span>
                </button>
              )}

              {canCancelOrder && (
                <button
                  onClick={handleNewOrderCancel}
                  className="w-full px-5 py-4 bg-red-50 hover:bg-red-100 text-red-700 font-bold rounded-xl transition-all border-2 border-red-200 hover:border-red-300 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <XCircle size={22} />
                    <div className="text-left">
                      <div className="font-bold">Cancel Order</div>
                      <div className="text-xs text-red-500 font-normal">Discard items and start fresh</div>
                    </div>
                  </div>
                </button>
              )}

              <button
                onClick={() => setShowNewOrderConfirm(false)}
                className="w-full px-5 py-3 text-slate-600 font-semibold hover:bg-slate-100 rounded-xl transition-all"
              >
                ← Continue with Current Order
                <span className="ml-2 text-[10px] font-bold bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">Esc</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Draft KOT Confirmation Modal - RED THEME */}
      {showDraftKOTConfirm && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onKeyDown={(e) => {
            if (e.key === 'Escape' && !isSendingDraftKOT) {
              setShowDraftKOTConfirm(false);
              setDraftKOTAction(null);
            }
          }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5">
            <div className="flex items-center gap-4 pb-4 border-b border-slate-200">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertCircle size={28} className="text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-slate-900">Items Not Sent to Kitchen</h3>
                <p className="text-sm text-slate-600 mt-1">
                  There are items in the cart that haven't been sent to the kitchen yet. Do you still want to proceed?
                </p>
              </div>
            </div>

            <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
              <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">Unsent Items</p>
              <div className="space-y-1.5 max-h-32 overflow-y-auto">
                {getCurrentCartItems()
                  .filter(item => !item.sentToKitchen)
                  .map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-slate-700">{item.quantity}x {item.name}</span>
                      <span className="text-slate-500 font-medium">₹{((item.finalPrice || item.price) * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleDraftKOTConfirm}
                disabled={isSendingDraftKOT}
                autoFocus
                className="w-full px-5 py-4 text-white font-bold rounded-xl transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 focus:ring-red-400"
              >
                {isSendingDraftKOT ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    {draftKOTAction === 'printReceipt' && <Printer size={20} />}
                    {draftKOTAction === 'eBill' && <Send size={20} />}
                    {draftKOTAction === 'settle' && <Receipt size={20} />}
                    {draftKOTAction === 'printReceipt' ? 'Yes, Print Receipt' : draftKOTAction === 'eBill' ? 'Yes, Send e-Bill' : 'Yes, Settle Payment'}
                  </>
                )}
              </button>

              <button
                onClick={() => {
                  setShowDraftKOTConfirm(false);
                  setDraftKOTAction(null);
                }}
                disabled={isSendingDraftKOT}
                className="w-full px-5 py-3 text-slate-600 font-semibold hover:bg-slate-100 rounded-xl transition-all disabled:opacity-40"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Keyboard Shortcuts Help Modal - RED THEME */}
      {showShortcutsHelp && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header - Red Theme */}
            <div className="bg-gradient-to-b from-red-500 to-red-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <Keyboard size={22} className="text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Keyboard Shortcuts</h3>
                  <p className="text-xs text-red-100">Quick actions for faster service</p>
                </div>
              </div>
              <button
                onClick={() => setShowShortcutsHelp(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X size={20} className="text-white" />
              </button>
            </div>

            <div className="p-4 space-y-2 max-h-[70vh] overflow-y-auto">
              <div className="space-y-1">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-2 mb-2">Kitchen</p>
                {canSendKOTPerm && (
                  <div className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg border border-slate-100">
                    <div className="flex items-center gap-3">
                      <Send size={16} className="text-slate-600" />
                      <span className="text-sm font-medium text-slate-700">Send KOT</span>
                    </div>
                    <kbd className="px-2 py-1 bg-slate-600 text-white text-xs font-bold rounded">F1</kbd>
                  </div>
                )}
              </div>

              {!isCounterMode && (canHoldOrder || canSplitTable || canCancelOrder || canCreateOrder) && (
                <div className="space-y-1 mt-3">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-2 mb-2">Order</p>

                  {canHoldOrder && (
                    <div className="flex items-center justify-between px-3 py-2 bg-amber-50 rounded-lg border border-amber-100">
                      <div className="flex items-center gap-3">
                        <Pause size={16} className="text-amber-600" />
                        <span className="text-sm font-medium text-slate-700">Hold / Resume Order</span>
                      </div>
                      <kbd className="px-2 py-1 bg-amber-500 text-white text-xs font-bold rounded">F3</kbd>
                    </div>
                  )}

                  {canSplitTable && (
                    <div className="flex items-center justify-between px-3 py-2 bg-red-50 rounded-lg border border-red-100">
                      <div className="flex items-center gap-3">
                        <Split size={16} className="text-red-600" />
                        <span className="text-sm font-medium text-slate-700">Split Table</span>
                      </div>
                      <kbd className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded">F5</kbd>
                    </div>
                  )}

                  {canCancelOrder && (
                    <div className="flex items-center justify-between px-3 py-2 bg-red-50 rounded-lg border border-red-100">
                      <div className="flex items-center gap-3">
                        <XCircle size={16} className="text-red-600" />
                        <span className="text-sm font-medium text-slate-700">Cancel Order</span>
                      </div>
                      <kbd className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded">F6</kbd>
                    </div>
                  )}

                  {canCreateOrder && (
                    <div className="flex items-center justify-between px-3 py-2 bg-red-50 rounded-lg border border-red-100">
                      <div className="flex items-center gap-3">
                        <FilePlus size={16} className="text-red-600" />
                        <span className="text-sm font-medium text-slate-700">New Order</span>
                      </div>
                      <kbd className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded">F7</kbd>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-1 mt-3">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-2 mb-2">Billing</p>

                {!isCounterMode ? (
                  <>
                    {canPrintBill && (
                      <div className="flex items-center justify-between px-3 py-2 bg-red-50 rounded-lg border border-red-100">
                        <div className="flex items-center gap-3">
                          <Printer size={16} className="text-red-600" />
                          <span className="text-sm font-medium text-slate-700">Print Receipt</span>
                        </div>
                        <kbd className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded">F8</kbd>
                      </div>
                    )}

                    {canSendEBill && (
                      <div className="flex items-center justify-between px-3 py-2 bg-red-50 rounded-lg border border-red-100">
                        <div className="flex items-center gap-3">
                          <Send size={16} className="text-red-600" />
                          <span className="text-sm font-medium text-slate-700">Send e-Bill</span>
                        </div>
                        <kbd className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded">F9</kbd>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="px-3 py-2 bg-slate-100 rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-500 text-center">
                      In Counter mode, receipt delivery options are available in Settle Payment (F4)
                    </p>
                  </div>
                )}

                {canSettle && (
                  <div className="flex items-center justify-between px-3 py-2 bg-red-50 rounded-lg border border-red-100">
                    <div className="flex items-center gap-3">
                      <Receipt size={16} className="text-red-600" />
                      <span className="text-sm font-medium text-slate-700">Settle Payment</span>
                    </div>
                    <kbd className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded">F4</kbd>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-3 border-t border-slate-200">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-2 mb-2">Menu Items</p>
                <div className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="flex items-center gap-3">
                    <Hash size={16} className="text-slate-500" />
                    <span className="text-sm font-medium text-slate-700">Quick add item by number</span>
                  </div>
                  <kbd className="px-2 py-1 bg-slate-600 text-white text-xs font-bold rounded">1-9</kbd>
                </div>
              </div>
            </div>

            <div className="px-6 py-3 bg-slate-50 border-t border-slate-200">
              <p className="text-xs text-slate-500 text-center">
                Press <kbd className="px-1.5 py-0.5 bg-slate-200 text-slate-600 rounded text-[10px] font-bold">Esc</kbd> or click outside to close
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}